/**
 * The reports hub (§11.1, P3-TR5).
 *
 * A date range and the reports that hang off it. The P&L basis is a visible
 * toggle rather than a setting, because P3-BR5 turns on the reader knowing
 * which question they are looking at the answer to — the same month is a
 * profit on one basis and a loss on the other, and a partner shown the second
 * without warning concludes the software is wrong.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { formatMoney, isNegativeAmount } from '../../lib/currency.js';
import { todayInDhaka } from '../../lib/date.js';
import { downloadFile } from '../../lib/download.js';
import { usePnl } from '../sales/api.js';

/** The month `today` falls in, which is the range a reports hub opens on. */
export function currentMonthRange(today: string): { from: string; to: string } {
  const date = new Date(`${today}T00:00:00.000Z`);
  const first = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));

  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}

export function ReportsHub(): JSX.Element {
  const { t } = useTranslation();

  const initial = currentMonthRange(todayInDhaka());
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [basis, setBasis] = useState<'cash' | 'accrual'>('cash');

  const { data: pnl, isLoading } = usePnl(from, to, basis);

  /** Which button is working, so only that one shows it. */
  const [downloading, setDownloading] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  /*
   * Fetched through the authenticated client, not linked to.
   *
   * An anchor cannot carry the bearer token — it lives in memory and goes on
   * as a header — so the previous links returned 401 and downloaded nothing.
   */
  async function download(kind: string, format: 'pdf' | 'xlsx'): Promise<void> {
    setDownloading(`${kind}-${format}`);
    setErrorKey(null);

    try {
      await downloadFile(`/exports/${kind}`, { from, to, basis, format }, `${kind}-${from}.${format}`);
    } catch (error) {
      setErrorKey(errorMessageKey(error));
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('reports.title')}</h1>

      <div className="mb-4 flex gap-2">
        <label className="flex-1">
          <span className="mb-1 block text-xs text-gray-500">{t('reports.from')}</span>
          <input
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2"
          />
        </label>
        <label className="flex-1">
          <span className="mb-1 block text-xs text-gray-500">{t('reports.to')}</span>
          <input
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2"
          />
        </label>
      </div>

      {/* P3-BR5 — the basis is a visible choice, not a hidden default. */}
      <div className="mb-3 flex gap-2" role="group" aria-label={t('reports.basis')}>
        {(['cash', 'accrual'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setBasis(option)}
            aria-pressed={basis === option}
            className={`min-h-touch flex-1 rounded-lg px-3 py-2 text-sm ${
              basis === option ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
            }`}
          >
            {t(`reports.basis.${option}`)}
          </button>
        ))}
      </div>

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {pnl && (
        <>
          {/* The sentence, not only the label: it is what stops a partner
              reading an accrual loss as a bug. */}
          <p className="mb-3 rounded-lg bg-gray-100 px-3 py-2 text-xs text-gray-600">{pnl.basisNote}</p>

          <section className="mb-4 rounded-xl bg-white p-4 shadow-sm" aria-label={t('reports.pnl')}>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">{t('reports.income')}</dt>
                <dd className="tabular-nums text-gray-900">{formatMoney(pnl.income)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">{t('reports.expenses')}</dt>
                <dd className="tabular-nums text-gray-900">{formatMoney(pnl.expenses)}</dd>
              </div>
            </dl>

            <div className="mt-3 flex justify-between border-t border-gray-200 pt-3">
              <span className="font-medium">{t('reports.netProfit')}</span>
              <span
                className={`text-lg font-semibold tabular-nums ${
                  isNegativeAmount(pnl.netProfit) ? 'text-red-700' : 'text-green-700'
                }`}
              >
                {formatMoney(pnl.netProfit)}
              </span>
            </div>
          </section>

          <section className="mb-4 rounded-xl bg-white p-4 shadow-sm" aria-label={t('reports.byCategory')}>
            <h2 className="mb-2 text-sm font-medium text-gray-700">{t('reports.byCategory')}</h2>
            <ul className="divide-y divide-gray-100">
              {pnl.expensesByCategory.map((line) => (
                <li key={line.name} className="flex justify-between py-2 text-sm">
                  <span className="text-gray-700">{line.name}</span>
                  <span className="tabular-nums text-gray-900">{formatMoney(line.amount)}</span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      <section aria-label={t('reports.download')}>
        <h2 className="mb-2 text-sm font-medium text-gray-700">{t('reports.download')}</h2>
        <div className="grid grid-cols-2 gap-2">
          {[
            { kind: 'pnl', key: 'reports.pnl' },
            { kind: 'partner-statement', key: 'reports.partnerStatement' },
          ].flatMap((report) =>
            (['pdf', 'xlsx'] as const).map((format) => {
              const id = `${report.kind}-${format}`;
              return (
                <button
                  key={id}
                  type="button"
                  // A button, not a link: this fetches through the
                  // authenticated client rather than navigating.
                  onClick={() => void download(report.kind, format)}
                  disabled={downloading !== null}
                  className="flex min-h-touch items-center justify-center rounded-xl bg-white px-3 py-3 text-sm text-gray-700 shadow-sm disabled:opacity-50"
                >
                  {downloading === id ? t('reports.preparing') : `${t(report.key)} · ${format.toUpperCase()}`}
                </button>
              );
            }),
          )}
        </div>

        {errorKey !== null && (
          <div role="alert" className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
            <p>{t(errorKey)}</p>
          </div>
        )}
      </section>
    </div>
  );
}
